import React, { useMemo, useState } from 'react';
import { format } from 'date-fns';
import { motion } from 'framer-motion';
import { Users, CalendarDays, Clock, History, PieChart, ChevronRight } from 'lucide-react';
import { User, OOORequest, Role } from '../types';
import { Badge, Modal, ProgressBar } from './ui';
import { BalanceEditor } from './BalanceEditor';
interface DashboardProps {
  currentUser: User;
  managedRole: Role | null;
  allUsers: User[];
  allRequests: OOORequest[];
  onUpdateBalance?: (userId: string, ptoTotal: number, ptoUsed: number) => void;
}
export function Dashboard({
  currentUser,
  managedRole,
  allUsers,
  allRequests,
  onUpdateBalance
}: DashboardProps) {
  const isManager = managedRole !== null;
  // --- MANAGER DASHBOARD ---
  const team = useMemo(() => {
    if (!isManager) return [];
    return allUsers.filter(u => u.role === managedRole);
  }, [allUsers, isManager, managedRole]);
  // Loan Officers do not accrue PTO; only count members who have a balance.
  const teamHasBalances = useMemo(() => team.some(u => typeof u.ptoTotal === 'number'), [team]);
  const teamStats = useMemo(() => {
    const totalAllotted = team.reduce((sum, u) => sum + (u.ptoTotal || 0), 0);
    const totalUsed = team.reduce((sum, u) => sum + (u.ptoUsed || 0), 0);
    return {
      size: team.length,
      used: totalUsed,
      remaining: totalAllotted - totalUsed,
      total: totalAllotted
    };
  }, [team]);
  // --- INDIVIDUAL DASHBOARD ---
  const myRequests = useMemo(() => {
    if (isManager) return [];
    return allRequests.filter(r => r.userId === currentUser.id).sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [allRequests, currentUser.id, isManager]);
  // Member whose request history is being viewed (manager drill-in)
  const [selectedMember, setSelectedMember] = useState<User | null>(null);
  const selectedMemberRequests = useMemo(() => {
    if (!selectedMember) return [];
    return allRequests.filter(r => r.userId === selectedMember.id).sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());
  }, [allRequests, selectedMember]);
  const formatDate = (dateStr: string) => {
    try {
      return format(new Date(dateStr), 'MMM d, yyyy h:mm a');
    } catch {
      return dateStr;
    }
  };
  if (isManager) {
    return <motion.div initial={{
      opacity: 0,
      y: 10
    }} animate={{
      opacity: 1,
      y: 0
    }} className="space-y-6" data-id="element-233">
        <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-234">
          Team PTO Dashboard
        </h2>

        {/* Stat Cards */}
        <div className={`grid grid-cols-1 gap-4 ${teamHasBalances ? 'md:grid-cols-3' : 'md:grid-cols-1'}`} data-id="element-235">
          <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4" data-id="element-236">
            <div className="p-3 bg-blue-50 text-[#0176d3] rounded-full" data-id="element-237">
              <Users className="w-6 h-6" data-id="element-238" />
            </div>
            <div data-id="element-239">
              <p className="text-sm font-medium text-gray-500" data-id="element-240">Team Members</p>
              <p className="text-2xl font-bold text-gray-900" data-id="element-241">
                {teamStats.size}
              </p>
            </div>
          </div>
          {teamHasBalances && <>
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4" data-id="element-242">
                <div className="p-3 bg-amber-50 text-amber-600 rounded-full" data-id="element-243">
                  <CalendarDays className="w-6 h-6" data-id="element-244" />
                </div>
                <div data-id="element-245">
                  <p className="text-sm font-medium text-gray-500" data-id="element-246">
                    Total PTO Used
                  </p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-247">
                    {teamStats.used}{' '}
                    <span className="text-sm font-normal text-gray-500" data-id="element-248">
                      days
                    </span>
                  </p>
                </div>
              </div>
              <div className="bg-white p-6 rounded-lg border border-gray-200 shadow-sm flex items-center space-x-4" data-id="element-249">
                <div className="p-3 bg-emerald-50 text-emerald-600 rounded-full" data-id="element-250">
                  <PieChart className="w-6 h-6" data-id="element-251" />
                </div>
                <div data-id="element-252">
                  <p className="text-sm font-medium text-gray-500" data-id="element-253">
                    Total PTO Remaining
                  </p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-254">
                    {teamStats.remaining}{' '}
                    <span className="text-sm font-normal text-gray-500" data-id="element-255">
                      days
                    </span>
                  </p>
                </div>
              </div>
            </>}
        </div>

        {/* Team List */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" data-id="element-256">
          <div className="px-6 py-4 border-b border-gray-200 bg-gray-50" data-id="element-257">
            <h3 className="text-lg font-medium text-gray-900" data-id="element-258">
              Direct Reports Balances
            </h3>
          </div>
          {team.length === 0 ? <div className="p-12 text-center" data-id="element-259">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-260" />
              <p className="text-gray-500" data-id="element-261">No direct reports found.</p>
            </div> : <ul className="divide-y divide-gray-200" data-id="element-262">
              {team.map(user => <li key={user.id} data-id="element-263">
                  <button type="button" onClick={() => setSelectedMember(user)} aria-label={`View past requests for ${user.name}`} className="w-full text-left p-6 hover:bg-gray-50 transition-colors flex flex-col md:flex-row md:items-center justify-between gap-6 focus:outline-none focus:bg-gray-50 focus:ring-2 focus:ring-inset focus:ring-[#0176d3]" data-id="element-264">
                    <div className="md:w-1/3" data-id="element-265">
                      <h4 className="text-base font-semibold text-gray-900" data-id="element-266">
                        {user.name}
                      </h4>
                      <p className="text-sm text-gray-500" data-id="element-267">{user.role}</p>
                    </div>
                    <div className="md:w-2/3 max-w-md w-full flex items-center gap-4" data-id="element-268">
                      <div className="flex-1" data-id="element-269">
                        {typeof user.ptoTotal === 'number' ? <ProgressBar used={user.ptoUsed || 0} total={user.ptoTotal} data-id="element-270" /> : <span className="text-sm text-gray-400 italic" data-id="element-271">
                            No PTO balance
                          </span>}
                      </div>
                      <ChevronRight className="w-5 h-5 text-gray-400 flex-shrink-0" data-id="element-272" />
                    </div>
                  </button>
                </li>)}
            </ul>}
        </div>

        {/* Team Member Request History Modal */}
        <Modal isOpen={selectedMember !== null} onClose={() => setSelectedMember(null)} title={selectedMember ? `${selectedMember.name} — PTO Request History` : 'PTO Request History'} maxWidth="max-w-2xl" data-id="element-273">
          {selectedMember && <div className="space-y-4" data-id="element-274">
              {/* Balance summary (editable for managers) */}
              <BalanceEditor user={allUsers.find(u => u.id === selectedMember.id) || selectedMember} onUpdateBalance={onUpdateBalance} data-id="element-275" />

              {selectedMemberRequests.length === 0 ? <div className="p-10 text-center" data-id="element-276">
                  <CalendarDays className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-277" />
                  <p className="text-gray-500" data-id="element-278">No past requests found.</p>
                </div> : <ul className="divide-y divide-gray-200 border border-gray-200 rounded-md overflow-hidden" data-id="element-279">
                  {selectedMemberRequests.map(request => <li key={request.id} className="p-4 space-y-3" data-id="element-280">
                      <Badge status={request.status} data-id="element-281" />
                      <div className="flex items-center text-sm text-gray-600" data-id="element-282">
                        <div className="flex items-center bg-gray-100 px-2.5 py-1 rounded" data-id="element-283">
                          <Clock className="w-4 h-4 mr-2 text-gray-500" data-id="element-284" />
                          <span className="font-medium" data-id="element-285">
                            {formatDate(request.startDate)}
                          </span>
                          <span className="mx-2 text-gray-400" data-id="element-286">→</span>
                          <span className="font-medium" data-id="element-287">
                            {formatDate(request.endDate)}
                          </span>
                        </div>
                      </div>
                      <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-100" data-id="element-288">
                        {request.message}
                      </div>
                      {request.status === 'Approved' && request.coverageIds && request.coverageIds.length > 0 && <div className="text-sm text-gray-600" data-id="element-289">
                            <span className="font-medium mr-2" data-id="element-290">
                              Coverage assigned:
                            </span>
                            {request.coverageIds.map(id => allUsers.find(u => u.id === id)?.name).join(', ')}
                          </div>}
                    </li>)}
                </ul>}
            </div>}
        </Modal>
      </motion.div>;
  }
  // --- INDIVIDUAL DASHBOARD ---
  // Loan Officers do not accrue PTO, so they have no balance to display.
  const hasBalance = typeof currentUser.ptoTotal === 'number';
  const myTotal = currentUser.ptoTotal || 0;
  const myUsed = currentUser.ptoUsed || 0;
  const myRemaining = myTotal - myUsed;
  return <motion.div initial={{
    opacity: 0,
    y: 10
  }} animate={{
    opacity: 1,
    y: 0
  }} className="space-y-8" data-id="element-291">
      <h2 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-292">
        My PTO Dashboard
      </h2>

      {/* Personal Balance Summary */}
      {hasBalance && <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden" data-id="element-293">
          <div className="p-6 md:p-8 flex flex-col md:flex-row items-center gap-8" data-id="element-294">
            <div className="flex-shrink-0 relative w-32 h-32 flex items-center justify-center" data-id="element-295">
              <svg className="w-full h-full transform -rotate-90" viewBox="0 0 36 36" data-id="element-296">
                <path className="text-gray-100" strokeWidth="3" stroke="currentColor" fill="none" d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" data-id="element-297" />
                <motion.path className={myRemaining <= 3 ? 'text-red-500' : myRemaining <= 7 ? 'text-amber-500' : 'text-[#0176d3]'} strokeWidth="3" strokeDasharray={`${myUsed / myTotal * 100}, 100`} stroke="currentColor" fill="none" initial={{
              strokeDasharray: '0, 100'
            }} animate={{
              strokeDasharray: `${myUsed / myTotal * 100}, 100`
            }} transition={{
              duration: 1,
              ease: 'easeOut'
            }} d="M18 2.0845 a 15.9155 15.9155 0 0 1 0 31.831 a 15.9155 15.9155 0 0 1 0 -31.831" data-id="element-298" />
              </svg>
              <div className="absolute flex flex-col items-center justify-center" data-id="element-299">
                <span className="text-2xl font-bold text-gray-900" data-id="element-300">
                  {myRemaining}
                </span>
                <span className="text-xs text-gray-500 uppercase tracking-wide" data-id="element-301">
                  Left
                </span>
              </div>
            </div>

            <div className="flex-1 grid grid-cols-2 gap-6 w-full" data-id="element-302">
              <div data-id="element-303">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-304">
                  Total Allowance
                </p>
                <p className="text-3xl font-semibold text-gray-900" data-id="element-305">
                  {myTotal}{' '}
                  <span className="text-base font-normal text-gray-500" data-id="element-306">
                    days
                  </span>
                </p>
              </div>
              <div data-id="element-307">
                <p className="text-sm font-medium text-gray-500 mb-1" data-id="element-308">
                  Days Used
                </p>
                <p className="text-3xl font-semibold text-gray-900" data-id="element-309">
                  {myUsed}{' '}
                  <span className="text-base font-normal text-gray-500" data-id="element-310">
                    days
                  </span>
                </p>
              </div>
            </div>
          </div>
        </div>}

      {/* Request History */}
      <div data-id="element-311">
        <div className="flex items-center mb-4" data-id="element-312">
          <History className="w-5 h-5 text-gray-500 mr-2" data-id="element-313" />
          <h3 className="text-lg font-bold text-gray-900" data-id="element-314">
            PTO Request History
          </h3>
        </div>

        <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden" data-id="element-315">
          {myRequests.length === 0 ? <div className="p-12 text-center" data-id="element-316">
              <CalendarDays className="w-12 h-12 text-gray-300 mx-auto mb-4" data-id="element-317" />
              <p className="text-gray-500" data-id="element-318">You have no PTO request history.</p>
            </div> : <ul className="divide-y divide-gray-200" data-id="element-319">
              {myRequests.map(request => <li key={request.id} className="p-6 hover:bg-gray-50 transition-colors" data-id="element-320">
                  <div className="flex flex-col md:flex-row md:items-start justify-between gap-4" data-id="element-321">
                    <div className="flex-1 space-y-3" data-id="element-322">
                      <div className="flex items-center space-x-3" data-id="element-323">
                        <Badge status={request.status} data-id="element-324" />
                      </div>

                      <div className="flex items-center space-x-4 text-sm text-gray-600" data-id="element-325">
                        <div className="flex items-center bg-gray-100 px-2.5 py-1 rounded" data-id="element-326">
                          <Clock className="w-4 h-4 mr-2 text-gray-500" data-id="element-327" />
                          <span className="font-medium" data-id="element-328">
                            {formatDate(request.startDate)}
                          </span>
                          <span className="mx-2 text-gray-400" data-id="element-329">→</span>
                          <span className="font-medium" data-id="element-330">
                            {formatDate(request.endDate)}
                          </span>
                        </div>
                      </div>

                      <div className="text-sm text-gray-700 bg-gray-50 p-3 rounded border border-gray-100" data-id="element-331">
                        {request.message}
                      </div>

                      {request.status === 'Approved' && request.coverageIds && request.coverageIds.length > 0 && <div className="text-sm text-gray-600 flex items-center mt-2" data-id="element-332">
                            <span className="font-medium mr-2" data-id="element-333">
                              Coverage assigned:
                            </span>
                            {request.coverageIds.map(id => allUsers.find(u => u.id === id)?.name).join(', ')}
                          </div>}
                    </div>
                  </div>
                </li>)}
            </ul>}
        </div>
      </div>
    </motion.div>;
}